/**
 * @ram/api-client — typed REST client generated from the backend OpenAPI
 * schema (drf-spectacular). Run `pnpm gen:api` to refresh.
 *
 * Until the first generation, this package re-exports a thin Axios wrapper
 * so apps can already import a typed instance. Once `src/generated/` exists,
 * apps should switch to the generated APIs.
 */
import axios, { type AxiosInstance } from "axios";

// Per-request opt-out for the global request loader. Background pollers set
// `{ skipLoader: true }` so they don't flash the full-screen overlay; user
// actions (submits, navigation-triggered loads) leave it unset and show it.
declare module "axios" {
  export interface AxiosRequestConfig {
    skipLoader?: boolean;
  }
}

export interface CreateClientOptions {
  /** Backend base URL — e.g. http://localhost:8000 */
  baseURL: string;
  /** Send cookies (needed for SPA session auth, §0/3). */
  withCredentials?: boolean;
  /** CSRF cookie name — defaults to Django's `csrftoken`. */
  csrfCookieName?: string;
  /** CSRF header name — defaults to Django's `X-CSRFToken`. */
  csrfHeaderName?: string;
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.split("; ").find((c) => c.startsWith(name + "="));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
}

export function createApiClient(options: CreateClientOptions): AxiosInstance {
  const csrfCookie = options.csrfCookieName ?? "csrftoken";
  const csrfHeader = options.csrfHeaderName ?? "X-CSRFToken";
  const instance = axios.create({
    baseURL: options.baseURL,
    withCredentials: options.withCredentials ?? true,
    timeout: 30_000,
  });

  instance.interceptors.request.use((config) => {
    if (config.method && !["get", "head", "options"].includes(config.method.toLowerCase())) {
      const token = readCookie(csrfCookie);
      if (token) config.headers.set(csrfHeader, token);
    }
    return config;
  });

  return instance;
}

// ── Error helpers ─────────────────────────────────────────────────────

/**
 * Normalised shape for any failed API call. Discriminating on `kind` lets
 * the UI decide whether to retry, ask the user to log back in, or render a
 * field-level validation error.
 *
 * - `network`    — request never reached the server (DNS/CORS/proxy down,
 *                  user's wifi cut, browser blocked). Caller should suggest
 *                  retry, not "wrong password".
 * - `server`     — server reachable but didn't speak our envelope. Almost
 *                  always a 5xx with HTML or empty body. Retry-friendly.
 * - `auth`       — backend returned the standard envelope with a known auth
 *                  failure code (401, captcha, etc.). Use the envelope's
 *                  message verbatim — it's user-facing copy.
 * - `validation` — field-level errors (envelope `details` is a per-field map).
 * - `unknown`    — envelope without enough info to classify. Treat as `auth`
 *                  for messaging purposes.
 */
export type ApiErrorKind = "network" | "server" | "auth" | "validation" | "unknown";

export interface ApiError {
  kind: ApiErrorKind;
  /** User-facing copy. Already translated where possible. */
  message: string;
  /** Backend-supplied error code (e.g. `auth_failed`, `captcha_failed`). Empty for network/server. */
  code: string;
  /** HTTP status (0 for network errors). */
  status: number;
  /** Envelope `details` payload — typically per-field validation errors or `captcha_required` hints. */
  details?: Record<string, unknown>;
}

interface AxiosLikeError {
  code?: string;
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
  };
}

interface BackendErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
}

/**
 * First human-readable message out of a DRF-style `details` map, e.g.
 * `{orbit_username: ["Already linked to another account."]}` →
 * `"Orbit username: Already linked to another account."`. The field name is
 * prefixed (humanised) because DRF copy like "This field is required." is
 * ambiguous without it; `non_field_errors`/`detail` entries pass through
 * bare. Returns undefined when nothing in the map is a usable string.
 */
function firstFieldError(details: Record<string, unknown>): string | undefined {
  for (const [field, value] of Object.entries(details)) {
    const first = Array.isArray(value) ? value[0] : value;
    if (typeof first !== "string" || !first) continue;
    if (field === "non_field_errors" || field === "detail") return first;
    const label = field.replace(/_/g, " ");
    return `${label.charAt(0).toUpperCase()}${label.slice(1)}: ${first}`;
  }
  return undefined;
}

/**
 * Convert any thrown value from an Axios call into a discriminated `ApiError`.
 * Safe to call with non-Axios errors — they fall into `kind: "unknown"`.
 */
export function extractError(e: unknown): ApiError {
  const err = e as AxiosLikeError;

  // 1. Network — no response field at all.
  if (!err.response) {
    return {
      kind: "network",
      code: err.code ?? "ERR_NETWORK",
      status: 0,
      message: "Couldn't reach the server. Check your connection and try again.",
    };
  }

  const status = err.response.status ?? 0;
  const data = err.response.data as BackendErrorEnvelope | undefined;
  const envelope = data?.error;

  // 2. No envelope — server reached but spoke a different protocol.
  if (!envelope || typeof envelope !== "object") {
    if (status >= 500) {
      return {
        kind: "server",
        code: "server_error",
        status,
        message: "Backend error. Please try again in a moment.",
      };
    }
    return {
      kind: "unknown",
      code: "unknown",
      status,
      message: "Something went wrong. Please try again.",
    };
  }

  // 3. Standard envelope — classify by HTTP status.
  const message = envelope.message ?? "Something went wrong. Please try again.";
  const code = envelope.code ?? "unknown";
  const details = envelope.details;

  if (status === 400 && code === "invalid" && details && typeof details === "object") {
    // The envelope's top-level message is a generic "Validation failed." —
    // the actionable copy lives per-field in `details`, so surface the
    // first field's message for callers that render `message` directly.
    return { kind: "validation", code, status, message: firstFieldError(details) ?? message, details };
  }
  if (status === 401 || status === 403 || code.startsWith("auth_") || code === "use_orbit") {
    return { kind: "auth", code, status, message, details };
  }
  return { kind: "unknown", code, status, message, details };
}

